<template>
  <div>
    <div
      v-for="(message, index) in messages"
      :key="`temp-${index}`"
      class="mb-4"
    >
      <div class="d-flex flex-column align-end">
        <span
          :style="{ maxWidth: '80%' }"
          class="mb-2 text-body-2 grey darken-1 white--text pa-2 rounded-lg rounded-br-0"
        >
          {{ message.displayQuestion || message.question }}
        </span>
        <span
          class="text-caption text--secondary mb-2"
        >
          {{ formattedDate(message.created_at) }}
        </span>
      </div>

      <div
        class="d-flex flex-column align-start pa-2"
      >
        <div
          v-if="message.is_pending && !message.answer"
          class="pa-3 rounded-lg grey lighten-5 d-flex align-center"
        >
          <img
            src="/static/chatbot/gifs/rotating_fox.gif"
            alt="processing"
            width="24"
            height="24"
          >
          <span class="ml-2 text-body-2">
            {{ getThinkingText(index) }}
          </span>
        </div>

        <div
          v-else-if="isMessageGated(message, index) && !shouldShowFaqInlinePaywall(message, index)"
          class="white px-2 pb-0 pt-4 rounded-lg markdown-scroll"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="markdown-body primary-grey--text pa-4"
            v-html="renderMarkdown(extractTitleBlock(message.answer))"
          />
          <div class="gated-container mt-0">
            <div
              class="white px-2 pb-0 pt-4 rounded-lg markdown-scroll gated-body"
              :style="getGatedBodyStyle(index)"
            >
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="markdown-body primary-grey--text pa-4"
                v-html="renderMarkdown(bodyAfterTitle(message.answer))"
              />
            </div>
            <div class="gated-overlay">
              <div
                :ref="`overlayInner-${index}`"
                class="gated-overlay-inner"
              >
                <Paywall />
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="!isPaywallMessage(message) && !isOverlayGatedActive(message)"
          class="white px-2 pb-0 pt-4 rounded-lg markdown-scroll"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="markdown-body primary-grey--text pa-4"
            v-html="renderMarkdown(message.answer)"
          />

          <div
            v-if="flags['power_bi_url_sinoa'] && message.power_bi_url"
            class="power-bi-container mb-4 pl-2"
            :style="containerStyle"
          >
            <div
              v-if="$isMobile"
              class="px-2 pb-2 primary--text font-weight-bold markdown-body"
            >
              For optimal visualisation, we recommend viewing this chart on a desktop
            </div>
            <iframe
              :style="iframeStyle"
              :src="message.power_bi_url"
              frameborder="0"
              scrolling="no"
              class="power-bi-iframe"
            />
          </div>
        </div>

        <template
          v-if="!message.is_pending
            && !isPaywallMessage(message)
            && !isMessageWithStoredAnswer(message)
          "
        >
          <div
            v-if="message.source === 'stored'"
            class="mt-2 text-caption grey--text"
            :class="{ 'd-flex flex-column': $isMobile }"
          >
            <span
              class="mr-2 text--lighten-1"
              :class="{ 'mb-3': $isMobile }"
            >
              Answered on: {{ formattedDate(message.created_at) }}
            </span>
            <span>
              <v-icon
                small
                :color="COLORS.inflow"
                class="mb-1"
              >
                mdi-information
              </v-icon>
              <span class="text-caption primary-grey--text">
                This is a historical response and may be outdated.
              </span>
            </span>
          </div>
          <div
            class="d-flex align-center mt-1"
            :style="{ gap: '12px' }"
          >
            <v-icon
              small
              class="mr-1"
              :color="isMessagePinned(message) ? 'primary' : ''"
              style="cursor: pointer;"
              @click="togglePin(message)"
            >
              mdi-pin-outline
            </v-icon>
            <span
              v-if="fullScreen"
              class="text-caption grey--text text--darken-1 font-weight-medium"
              :class="{ 'primary--text': isMessagePinned(message) }"
              style="cursor: pointer;"
              @click="togglePin(message)"
            >
              {{ pinText(message) }}
            </span>

            <v-btn
              v-if="message.source === 'stored'"
              text
              class="text-none grey--text text--darken-1 text-caption font-weight-medium"
              :disabled="isLimitReached"
              @click="handleRegenerate(message)"
            >
              <v-icon
                small
                class="mr-1"
              >
                mdi-repeat
              </v-icon>
              Regenerate with latest data
            </v-btn>
          </div>
        </template>

        <div
          v-if="shouldShowFeedback(message)"
          class="d-flex align-center mt-1"
          :style="{ gap: '4px' }"
        >
          <span class="text-caption grey--text text--darken-1 mr-1">
            Do you like this answer?
          </span>
          <v-btn
            icon
            x-small
            @click="submitFeedback(message.id, 1)"
          >
            <v-icon
              small
              :color="getFeedbackValue(message.id) === 1 ? 'green' : 'grey darken-1'"
            >
              mdi-thumb-up
            </v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            @click="submitFeedback(message.id, -1)"
          >
            <v-icon
              small
              :color="getFeedbackValue(message.id) === -1 ? 'red' : 'grey darken-1'"
            >
              mdi-thumb-down
            </v-icon>
          </v-btn>
        </div>

        <template
          v-if="!message.is_pending && !isPaywallMessage(message)"
        >
          <div
            v-if="shouldShowFaqInlinePaywall(message, index) && !isMessageGated(message, index)"
            class="my-2"
          >
            <Paywall @scroll-bottom="$emit('scroll-bottom')" />
          </div>
        </template>
      </div>
    </div>

    <FollowUpQuestionCarousel
      v-if="!isLimitReached && !showPaywall"
      @followup-click="$emit('followup-click', $event)"
    />

    <Paywall
      v-if="showGlobalPaywall()"
      class="my-8"
      @scroll-bottom="$emit('scroll-bottom')"
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { COLORS } from '../../helpers/colors';
import Paywall from './Paywall.vue';
import formattedDate from './data/helpers';
import MarkdownIt from 'markdown-it';
import FollowUpQuestionCarousel from './FollowUpQuestionCarousel.vue';

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

export default {
  name: 'ChatMessages',

  components: {
    Paywall,
    FollowUpQuestionCarousel,
  },

  props: {
    fullScreen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      COLORS,
      thinkingMessages: [
        'Analyzing futures and basis… your grain\'s story is unfolding',
        'Crunching global market trends — from Chicago to the Black Sea',
        'Checking the latest bids in your neck of the woods...',
        'Running the numbers so you don\'t have to',
        'Scanning USDA, StatsCan, and global trade data...',
        'Harvesting insights from today\'s market chatter',
        'Sorting the wheat from the chaff...',
        'Planting the seeds of your next market move...',
        'Letting the markets breathe while we run the combine',
        'Plowing through the data rows',
        'Shaking the grain bin for the latest prices',
        'Calibrating the auger... almost there',
        'Markets are in motion… so are we',
        'We\'re halfway through the yield check...',
        'Lining up the trucks — your answer\'s almost loaded',
        'The crop report is rolling in...',
        'We\'ve got a strong basis… for a strong answer',
      ],
      rotators: {},
      scrollInterval: null,
      overlayHeights: {},
    };
  },

  beforeDestroy() {
    this.stopAutoScroll();
    Object.keys(this.rotators).forEach((k) => this.stopRotator(Number(k)));
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  },

  computed: {
    ...mapState('chatbot', ['messages', 'showPaywall']),
    ...mapState('launchDarkly', ['flags']),
    ...mapGetters('chatbot', ['isLimitReached', 'getFeedbackForInteraction']),
    headerOffset() {
      return this.$headerOffset || 0;
    },

    containerStyle() {
      const height = this.$isMobile ? '50vh' : '70vh';
      return {
        position: 'relative',
        left: '0',
        width: '100%',
        overflow: 'hidden',
        height: `calc(${height} - ${this.headerOffset}px)`,
      };
    },

    iframeStyle() {
      const height = this.$isMobile ? '50vh' : '70vh';
      const extraSpace = this.$isMobile ? '37px' : '67px';
      return {
        width: '100%',
        height: `calc(${height} - ${this.headerOffset}px + ${extraSpace})`,
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      const idx = this.getNewestPaywallIndex();
      if (idx !== -1) this.updateOverlayHeight(idx);
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }
  },

  watch: {
    messages: {
      deep: true,
      immediate: true,
      handler(newMessages) {
        newMessages.forEach((msg, idx) => {
          if (msg?.is_pending && !msg.answer) {
            if (!this.rotators[idx]) {
              this.startRotator(idx);
            }
          } else {
            this.stopRotator(idx);
          }
          if (msg?.is_complete) {
            this.stopRotator(idx);
          }
        });
        Object.keys(this.rotators).forEach((key) => {
          const i = Number(key);
          if (!newMessages[i]) {
            this.stopRotator(i);
            this.$delete(this.rotators, key);
          }
        });

        const hasStreamingMessage = newMessages.some((msg) => msg.is_pending);
        if (hasStreamingMessage) {
          this.startAutoScroll();
        } else {
          this.stopAutoScroll();
        }
        this.$nextTick(() => {
          const idx = this.getNewestPaywallIndex();
          if (idx !== -1) this.updateOverlayHeight(idx);
        });
      },
    },
  },

  methods: {
    ...mapActions('chatbot', ['togglePinMessage', 'sendMessage', 'upsertFeedback']),
    ...mapMutations('chatbot', ['clearMessages']),

    startAutoScroll() {
      if (this.scrollInterval) return;

      this.scrollInterval = setInterval(() => {
        this.$emit('scroll-bottom');
      }, 50);
    },
    stopAutoScroll() {
      if (this.scrollInterval) {
        clearInterval(this.scrollInterval);
        this.scrollInterval = null;
      }
    },
    isMessageGated(message, idx) {
      if (!message) return false;
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || message.is_overlay_gated !== true) return false;
      if (typeof idx !== 'number' || idx < 0) return false;
      return idx === this.getNewestPaywallIndex();
    },

    isOverlayGatedActive(message) {
      const gated = (this.isLimitReached || this.showPaywall);
      return !!(gated && message?.is_overlay_gated === true);
    },

    extractTitleBlock(text) {
      if (!text) return '';
      if (!text.startsWith('#')) return '';
      const sepIndex = text.indexOf('\n\n');
      if (sepIndex !== -1) {
        return text.slice(0, sepIndex + 2).trimEnd();
      }
      return text.trimEnd();
    },

    bodyAfterTitle(text) {
      if (!text) return '';
      if (!text.startsWith('#')) return text;
      const sepIndex = text.indexOf('\n\n');
      if (sepIndex !== -1) {
        return text.slice(sepIndex + 2).trimStart();
      }
      return '';
    },

    anyGatedMessage() {
      const newestIdx = this.getNewestPaywallIndex();
      if (newestIdx === -1) return false;
      const m = this.messages[newestIdx];
      return !!(m && m.is_overlay_gated === true);
    },

    hasStreamingMessageWithPaywall() {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated) return false;
      return Array.isArray(this.messages) && this.messages.some((m) => m?.is_pending === true);
    },

    isMessagePinned(message) {
      return !!message.is_pinned;
    },

    isMessageWithStoredAnswer(message) {
      return message.question_type === 'qna' || message.question_type === 'faq';
    },

    isPaywallMessage(message) {
      return message.answer === 'Limit reached.';
    },

    shouldShowFeedback(message) {
      return !!(
        message.is_complete
        && message.id
        && !this.isPaywallMessage(message)
        && message.source !== 'stored'
      );
    },

    getFeedbackValue(interactionId) {
      if (!interactionId) return 0;
      const feedback = this.getFeedbackForInteraction(interactionId);
      return feedback?.value || 0;
    },

    async submitFeedback(interactionId, value) {
      if (!interactionId) return;

      try {
        await this.upsertFeedback({ interactionId, value });
      } catch (error) {
        // Error already handled in action
      }
    },

    pinText(message) {
      return this.isMessagePinned(message) ? 'Unpin' : 'Pin';
    },

    togglePin(message) {
      if (!message.id) return;
      this.togglePinMessage(message.id);
    },

    showGlobalPaywall() {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || this.hasStreamingMessageWithPaywall()) return false;
      // Only show global paywall when there is no inline (FAQ) or overlay (QnA) paywall to render
      return this.getNewestPaywallIndex() === -1;
    },

    isLastMessageFaq() {
      if (!Array.isArray(this.messages) || this.messages.length === 0) return false;
      const last = this.messages[this.messages.length - 1];
      return !!(last && last.question_type === 'faq');
    },

    hasInlineFaqAfter(idx) {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || !Array.isArray(this.messages)) return false;
      for (let i = idx + 1; i < this.messages.length; i += 1) {
        const m = this.messages[i];
        if (m?.question_type === 'faq' && m?.is_complete === true) return true;
      }
      return false;
    },

    hasAnyPaywallAfter(idx) {
      return this.hasOverlayGatedAfter(idx) || this.hasInlineFaqAfter(idx);
    },

    // Returns the index of the newest paywall candidate (prefer QnA overlay over FAQ inline)
    getNewestPaywallIndex() {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || !Array.isArray(this.messages) || this.messages.length === 0) return -1;

      // Prefer overlay-gated QnA if present (scan from end)
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        if (this.messages[i]?.is_overlay_gated === true) return i;
      }

      // Otherwise use newest FAQ inline (scan from end)
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        const m = this.messages[i];
        if (m?.question_type === 'faq' && m?.is_complete === true) return i;
      }

      return -1;
    },

    hasOverlayGatedAfter(idx) {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || !Array.isArray(this.messages)) return false;
      for (let i = idx + 1; i < this.messages.length; i += 1) {
        if (this.messages[i]?.is_overlay_gated === true) return true;
      }
      return false;
    },

    shouldShowFaqInlinePaywall(message, idx) {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || !message?.is_complete) return false;
      // Never render inline FAQ paywall for overlay-gated QnA messages
      if (message?.is_overlay_gated === true) return false;
      if (message?.question_type !== 'faq') return false;
      return idx === this.getNewestPaywallIndex();
    },

    hasFaqInlinePaywall() {
      const gated = (this.isLimitReached || this.showPaywall);
      if (!gated || !Array.isArray(this.messages)) return false;
      for (let i = 0; i < this.messages.length; i += 1) {
        if (this.shouldShowFaqInlinePaywall(this.messages[i], i)) return true;
      }
      return false;
    },

    renderMarkdown(text) {
      return md.render(text || '');
    },

    async handleRegenerate(message) {
      this.clearMessages();
      this.$emit('close-history');
      await this.sendMessage({
        question: message.displayQuestion || message.question,
      });
    },

    getThinkingText(index) {
      const r = this.rotators[index];
      return r?.currentText || this.thinkingMessages[0];
    },

    startRotator(index) {
      if (!this.rotators[index]) {
        this.$set(
          this.rotators,
          index,
          {
            timer: null,
            currentText: this.thinkingMessages[0],
            currentIndex: 0,
          },
        );
      }
      const delay = this.nextDelayMs();
      this.rotators[index].timer = setTimeout(() => {
        const msgObj = this.messages[index];
        if (msgObj && msgObj.is_pending) {
          this.tickRotator(index);
        } else {
          this.stopRotator(index);
        }
      }, delay);
    },

    stopRotator(index) {
      const r = this.rotators[index];
      if (r && r.timer) {
        clearTimeout(r.timer);
        r.timer = null;
      }
    },

    tickRotator(index) {
      const r = this.rotators[index];
      if (!r) return;

      const lastIdx = this.thinkingMessages.length - 1;
      const nextIdx = Math.min((r.currentIndex || 0) + 1, lastIdx);
      const nextText = this.thinkingMessages[nextIdx];

      this.$set(this.rotators, index, {
        ...r,
        currentText: nextText,
        currentIndex: nextIdx,
        timer: null,
      });

      if (nextIdx < lastIdx) {
        const delay = this.nextDelayMs();
        this.rotators[index].timer = setTimeout(() => {
          const msgObj = this.messages[index];
          if (msgObj && msgObj.is_pending) {
            this.tickRotator(index);
          } else {
            this.stopRotator(index);
          }
        }, delay);
      }
    },

    nextDelayMs() {
      const jitter = (Math.random() - 0.5) * 1200;
      return 3000 + Math.round(jitter);
    },

    getGatedBodyStyle(index) {
      const h = this.overlayHeights[index];
      if (typeof h === 'number' && h > 0) {
        return { maxHeight: `${h}px`, minHeight: `${h}px` };
      }
      return {};
    },

    updateOverlayHeight(index) {
      this.$nextTick(() => {
        const measure = () => {
          const ref = this.$refs[`overlayInner-${index}`];
          const el = Array.isArray(ref) ? ref[0] : ref;
          const h = el?.offsetHeight || el?.clientHeight || 0;
          if (h > 0) {
            this.$set(this.overlayHeights, index, h);
          }
        };
        measure();
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame?.(measure);
          setTimeout(measure, 120);
        }
      });
    },

    handleResize() {
      const idx = this.getNewestPaywallIndex();
      if (idx !== -1) {
        this.updateOverlayHeight(idx);
      }
    },

    formattedDate,
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors/colors";

::v-deep(.markdown-scroll) {
  overflow-x: auto;
  max-width: 90%;
  border: 1px solid colors.$greyLighten3 !important;
}

::v-deep(.markdown-body table) {
  display: block;
  overflow-x: auto;
  white-space: nowrap;
  max-width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
}

::v-deep(.markdown-body th),
::v-deep(.markdown-body td) {
  border: 1px solid colors.$greyLighten2;
  padding: 0.6em 0.8em;
  text-align: left;
}

::v-deep(.markdown-body th) {
  background-color: colors.$greyLighten3;
  font-weight: bold;
}

::v-deep(.markdown-body h1) {
  font-size: 1.6rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

::v-deep(.markdown-body h2) {
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
}

::v-deep(.markdown-body h3) {
  font-size: 1rem;
  font-weight: bold;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

::v-deep(.markdown-body h1),
::v-deep(.markdown-body h2),
::v-deep(.markdown-body h3) {
  line-height: 1.4;
}

::v-deep(.markdown-body ul) {
  padding-left: 1.25rem;
  margin-bottom: 1rem;
}

::v-deep(.markdown-body li) {
  margin-bottom: 0.5rem;
}

::v-deep(.markdown-body p) {
  margin-bottom: 1rem;
}

::v-deep(.markdown-body a) {
  color: colors.$ss4;
  text-decoration: underline;
}
.gated-container {
  position: relative;
  margin-bottom: 16px;
}

.gated-body {
  position: relative;
  filter: blur(6px);
  pointer-events: none;
  user-select: none;
}

.gated-body::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 56px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff);
}

.gated-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  z-index: 2;
}

.gated-overlay-inner {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

@media (max-width: 600px) {
  .gated-container {
    overflow: visible;
    margin-bottom: 8px;
  }

  .gated-overlay {
    justify-content: center;
  }

  .gated-overlay-inner {
    padding: 8px;
    max-width: 100%;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    box-sizing: border-box;
  }

}
@media (max-width: 600px) {
  ::v-deep(.markdown-scroll) {
    max-width: 100%;
    width: 100%;
  }
}
.power-bi-container {
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
}

.power-bi-iframe {
  width: 100%;
  height: 100%;
}

@media (max-width: 600px) {
  .power-bi-container {
    height: 250px;
    width: 100%;
  }
}
</style>
